import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { notificationsApi } from "@/api/notificationsApi";
import { useUserStore } from "@/store/userStore";
import type { UserNotification } from "@/types/userNotification";

export const USER_NOTIFICATIONS_QUERY_KEYS = {
  UNREAD: ["notifications", "unread"] as const,
};

export const selectUnseenFuelExhaustionNotifications = (
  notifications: readonly UserNotification[] | undefined,
  shownIds: ReadonlySet<number>
): UserNotification[] =>
  notifications?.filter(
    (notification) => notification.type === "fuel_exhausted" && !shownIds.has(notification.id)
  ) ?? [];

export const useFuelExhaustionNotification = () => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const queryClient = useQueryClient();
  const shownNotificationIds = useRef(new Set<number>());

  const notificationsQuery = useQuery({
    queryKey: USER_NOTIFICATIONS_QUERY_KEYS.UNREAD,
    queryFn: notificationsApi.listUnread,
    enabled: isAuthenticated,
    retry: false,
    refetchInterval: 15_000,
  });

  const { mutate: markRead } = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_NOTIFICATIONS_QUERY_KEYS.UNREAD });
    },
  });

  useEffect(() => {
    if (!isAuthenticated) {
      shownNotificationIds.current.clear();
      return;
    }

    const newNotifications = selectUnseenFuelExhaustionNotifications(
      notificationsQuery.data,
      shownNotificationIds.current
    );

    for (const notification of newNotifications) {
      shownNotificationIds.current.add(notification.id);
      toast.error("Топливо закончилось. Боты остановятся после завершения текущего цикла.");
      markRead(notification.id);
    }
  }, [isAuthenticated, markRead, notificationsQuery.data]);
};
